import type { CogniteClient } from '@cognite/sdk';
import type { MantineThemeOverride } from '@mantine/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { produce } from 'immer';
import { MotionConfig } from 'motion/react';
import { Suspense } from 'react';
import { I18nextProvider } from 'react-i18next';

import { LabeledLoader } from '#core/components/layout/LabeledLoader.tsx';
import { RootErrorBoundary } from '#core/components/layout/RootErrorBoundary.tsx';
import { CogniteClientContext } from '#core/contexts/CogniteClientContext.ts';
import { ProjectProvider } from '#core/contexts/ProjectProvider.tsx';
import { createI18n } from '#core/i18n/i18n.ts';
import { createQueryClient, motionConfigTransition, setupRuntime } from '#core/runtime.ts';
import type { ProjectConfigInput } from '#core/schema/ProjectConfigSchema.ts';

import { config as baseConfig } from '#config.ts';
import { AtlasAssistant, Chat, Tool } from '#plugin-chat';
import { views as viewsObject } from '#views.ts';

import classes from './AtlasChat.module.css';
import { EntityOverviewInstructions } from '#components/EntityOverviewInstructions.tsx';
import { ATLAS_CHAT_ROOT_CLASS } from '#embed/cssRegistry.ts';
import { resources } from '#i18n/resources.ts';
import { IpsoDomainInstructions } from '#instructions/IpsoDomain.tsx';
import { SafeInstructions } from '#instructions/SafeInstructions.tsx';
import { AppProviders } from '#instructions/virtualViews/AppProviders.tsx';

setupRuntime();

const views = [...Object.values(viewsObject)];
const queryClient = createQueryClient();
const i18n = createI18n(resources);

export interface AtlasChatProps {
  /** Authenticated Cognite SDK client used for all CDF and Atlas AI requests. */
  client: CogniteClient;
  /** Agent display name shown in the chat hero. Defaults to the project config name. */
  agentName?: string;
  /** Mantine theme override. Defaults to the AiPSO theme. */
  theme?: MantineThemeOverride;
  /** Defaults to the project config color scheme. */
  colorScheme?: 'light' | 'dark';
  /** In-shadow-root element the chat renders into. Scopes Mantine CSS variables, the color-scheme attribute, and portals to the shadow tree. */
  shadowTarget?: HTMLElement;
}

/**
 * The complete, self-contained Atlas agent chat: providers (query cache,
 * theme, i18n, motion), the chat session, the iPSO tools, and the domain
 * instructions. Mounted standalone inside the dashboard shell, or embedded
 * into a host app through `createAtlasChat`.
 */
export function AtlasChat({ client, agentName, theme, colorScheme, shadowTarget }: AtlasChatProps) {
  const config = produce(baseConfig as ProjectConfigInput, (draft) => {
    if (agentName) {
      draft.feature = {
        ...draft.feature,
        ai: {
          ...draft.feature?.ai,
          agent: {
            ...draft.feature?.ai?.agent,
            name: agentName,
          },
        },
      };
    }
    if (theme) {
      // biome-ignore lint/suspicious/noExplicitAny: FIXME Align with mantine's theme type
      draft.theme = theme as any;
    }
    if (colorScheme) {
      draft.defaultColorScheme = colorScheme;
    }
  });

  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ProjectProvider
          config={config}
          cssVariablesSelector={shadowTarget ? `.${ATLAS_CHAT_ROOT_CLASS}` : undefined}
          getRootElement={shadowTarget ? () => shadowTarget : undefined}
          portalTarget={shadowTarget}
        >
          <I18nextProvider i18n={i18n}>
            <MotionConfig transition={motionConfigTransition}>
              <CogniteClientContext value={client}>
                <Suspense fallback={<LabeledLoader label="Loading chat..." />}>
                  <div className={classes.chat}>
                    <Chat views={views}>
                      <AppProviders />
                      <AtlasAssistant>
                        <Tool.CreateResponse name="createResponse" />
                        <Tool.Plan name="plan" />
                        <Tool.DescribeViews name="describeViews" />
                        <Tool.QueryKnowledgeGraph name="queryKnowledgeGraph" />
                        <Tool.TransformDataset name="transformDataset" />
                        <Tool.AskUserQuestion name="askUserQuestion" />
                        <Tool.ShowDashboard name="showDashboard" />
                        <Tool.ShowInsights name="showInsights" />

                        <SafeInstructions>
                          <IpsoDomainInstructions />
                        </SafeInstructions>
                        <SafeInstructions>
                          <EntityOverviewInstructions />
                        </SafeInstructions>
                      </AtlasAssistant>
                    </Chat>
                  </div>
                </Suspense>
              </CogniteClientContext>
            </MotionConfig>
          </I18nextProvider>
        </ProjectProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}
